import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchActiveSpeakerManagementList, updateSpeakerStatus } from '../../api/speakerApi';
import '../../styles/components/SpeakerInvitationPopup.css';

// Helper to clean HTML from string
const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
};

const SpeakerInvitationPopup = ({ isOpen, onClose, onStatusChange, eventId }) => {
  const { t } = useTranslation();
  const [speakerData, setSpeakerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });

  const loadSpeakerData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchActiveSpeakerManagementList();
      console.log('📦 [Popup] Active speaker management list loaded:', data);
      setSpeakerData(data);
    } catch (error) {
      console.error('❌ [Popup] Failed to load speaker management data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadSpeakerData();
    }
  }, [isOpen, loadSpeakerData]);

  // Resolved Event ID for status updates
  const resolvedEventId = eventId || speakerData?.mySpeakerManagement?.[0]?.eventID;

  // Auto-close if speakerAcceptRequest is false or invitation is handled
  const needsAcceptance = speakerData?.speakerAcceptRequest === true || speakerData?.speakerAcceptRequest === 'true';

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: '', message: '' });
    }, 5000);
  };

  const handleAccept = async () => {
    if (!resolvedEventId) {
      showNotification('error', t('invalidEventId', 'Invalid Event ID.'));
      return;
    }
    setAcceptLoading(true);
    try {
      const result = await updateSpeakerStatus('Approved', resolvedEventId);
      console.log('✅ [Popup] Accept result:', result);
      
      const refreshed = await fetchActiveSpeakerManagementList();
      setSpeakerData(refreshed);
      
      showNotification('success', t('acceptSuccess', 'Invitation accepted successfully!'));
      
      if (onStatusChange) {
        onStatusChange('accepted');
      }
      
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('❌ [Popup] Accept failed:', error);
      showNotification('error', t('acceptError', 'Failed to accept invitation. Please try again.'));
    } finally {
      setAcceptLoading(false);
    }
  };

  const handleReject = async () => {
    if (!resolvedEventId) {
      showNotification('error', t('invalidEventId', 'Invalid Event ID.'));
      return;
    }
    setAcceptLoading(true);
    try {
      const result = await updateSpeakerStatus('Rejected', resolvedEventId);
      console.log('✅ [Popup] Reject result:', result);
      
      const refreshed = await fetchActiveSpeakerManagementList();
      setSpeakerData(refreshed);
      
      showNotification('info', t('rejectSuccess', 'Invitation rejected.'));
      
      if (onStatusChange) {
        onStatusChange('rejected');
      }
      
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('❌ [Popup] Reject failed:', error);
      showNotification('error', t('rejectError', 'Failed to reject invitation. Please try again.'));
    } finally {
      setAcceptLoading(false);
    }
  };

  if (!isOpen) return null;

  const confInfo = speakerData?.conferenceInformation || {};
  const logo = confInfo.conferenceLogo || '';
  const title = confInfo.invitationFor || 'Speaker Invitation';
  const datesText = confInfo.conferenceDates || '';
  const venueText = confInfo.conferenceVenue ? stripHtml(confInfo.conferenceVenue) : '';
  const subtitle = [datesText, venueText].filter(Boolean).join(' · ');

  const currentStatus = speakerData?.mySpeakerManagement?.[0]?.status || 'Pending';

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-container" onClick={(e) => e.stopPropagation()} style={{
        padding: 0,
        overflow: 'hidden',
        maxWidth: '400px',
        borderRadius: '24px',
        background: '#ffffff',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
        position: 'relative'
      }}>
        {/* Close Button */}
        <button 
          onClick={onClose} 
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'rgba(255, 255, 255, 0.25)',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 10,
            color: '#ffffff'
          }}
          aria-label={t('close', 'Close')}
        >
          <span className="material-icons" style={{ fontSize: '18px' }}>close</span>
        </button>

        {notification.show && (
          <div className={`popup-toast ${notification.type}`} style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            right: '20px',
            zIndex: 20
          }}>
            <span className="material-icons">
              {notification.type === 'success' ? 'check_circle' : 
               notification.type === 'error' ? 'error' : 'info'}
            </span>
            <span>{notification.message}</span>
          </div>
        )}

        {loading ? (
          <div className="popup-loading" style={{ padding: '60px 0' }}>
            <div className="popup-spinner"></div>
            <p>{t('loading', 'Loading...')}</p>
          </div>
        ) : speakerData ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Header Block with Gradient */}
            <div style={{
              height: '120px',
              background: 'linear-gradient(135deg, #1d4ed8 0%, #0d9488 100%)',
              width: '100%'
            }} />

            {/* Logo block overlapping the header */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '-50px',
              position: 'relative',
              zIndex: 5
            }}>
              <div style={{
                width: '100px',
                height: '100px',
                borderRadius: '16px',
                background: '#ffffff',
                border: '4px solid #ffffff',
                boxShadow: '0 8px 16px rgba(0,0,0,0.08)',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px'
              }}>
                {logo ? (
                  <img 
                    src={logo} 
                    alt={title} 
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <span className="material-icons" style={{ fontSize: '48px', color: '#cbd5e1' }}>groups</span>
                )}
              </div>
            </div>

            {/* Content Area */}
            <div style={{
              padding: '20px 24px 28px 24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center'
            }}>
              {/* Conference Name */}
              <h2 style={{
                fontSize: '18px',
                fontWeight: '700',
                color: '#1e293b',
                margin: '0 0 6px 0',
                lineHeight: '1.3'
              }}>
                {title}
              </h2>

              {/* Dates & Venue */}
              <p style={{
                fontSize: '13px',
                color: '#64748b',
                margin: '0 0 20px 0',
                fontWeight: '500'
              }}>
                {subtitle}
              </p>

              {/* Actions / Status */}
              {needsAcceptance ? (
                <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '12px' }}>
                  <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 8px 0' }}>
                    {t('invitationMessage', 'You have been invited as a speaker for this event. Would you like to accept this invitation?')}
                  </p>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', width: '100%' }}>
                    <button 
                      onClick={handleAccept}
                      disabled={acceptLoading}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        padding: '10px 16px',
                        background: '#1d4ed8',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      <span className="material-icons" style={{ fontSize: '16px' }}>
                        {acceptLoading ? 'hourglass_top' : 'check_circle'}
                      </span>
                      {acceptLoading ? t('processing', 'Processing...') : t('accept', 'Accept')}
                    </button>
                    <button 
                      onClick={handleReject}
                      disabled={acceptLoading}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        padding: '10px 16px',
                        background: '#f4f4f5',
                        color: '#ef4444',
                        border: '1px solid #e4e4e7',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      <span className="material-icons" style={{ fontSize: '16px' }}>cancel</span>
                      {t('reject', 'Reject')}
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 20px',
                  borderRadius: '30px',
                  background: currentStatus.toLowerCase() === 'approved' ? '#48bb78' : '#f56565',
                  color: '#ffffff',
                  fontWeight: '600',
                  fontSize: '13px',
                  boxShadow: 'none'
                }}>
                  <span className="material-icons" style={{ fontSize: '16px' }}>
                    {currentStatus.toLowerCase() === 'approved' ? 'check_circle' : 'cancel'}
                  </span>
                  {currentStatus}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="popup-error" style={{ padding: '60px 0' }}>
            <span className="material-icons" style={{ fontSize: '48px', color: '#cbd5e1' }}>error_outline</span>
            <p>{t('noData', 'No speaker data available')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpeakerInvitationPopup;